import { PaymentOptionPropertiesInterface } from '../../../services/exports/Interfaces';
import { PaymentMethod, PaymentMethodBrand } from '../../../services/exports/Constants';
import VisaIcon from '../../../assets/icons/payment-methods/VisaIcon';
import MasterCardIcon from '../../../assets/icons/payment-methods/MasterCardIcon';
import CashIcon from '../../../assets/icons/payment-methods/CashIcon';
import ApplePayIcon from '../../../assets/icons/payment-methods/ApplePayIcon';
import GooglePayIcon from '../../../assets/icons/payment-methods/GooglePayIcon';
import PayPalIcon from '../../../assets/icons/payment-methods/PayPalIcon';
import KlarnaIcon from '../../../assets/icons/payment-methods/KlarnaIcon';
import GiroPayIcon from '../../../assets/icons/payment-methods/GiroPayIcon';
import SepaDebitIcon from '../../../assets/icons/payment-methods/SepaDebitIcon';
import AmericanExpressIcon from '../../../assets/icons/payment-methods/AmericanExpressIcon';
import DiscoverIcon from '../../../assets/icons/payment-methods/DiscoverIcon';
import DinersClubIcon from '../../../assets/icons/payment-methods/DinersClubIcon';
import JcbIcon from '../../../assets/icons/payment-methods/JcbIcon';
import UnionPayIcon from '../../../assets/icons/payment-methods/UnionPayIcon';
import MaestroIcon from '../../../assets/icons/payment-methods/MaestroIcon';
import DankortIcon from '../../../assets/icons/payment-methods/DankortIcon';
import { ReactElement } from 'react';
import { ClassnameAwareComponentProps } from '../../types';
import PayByBankIcon from '../../../assets/icons/payment-methods/PayByBankIcon';
import IdealIcon from '../../../assets/icons/payment-methods/IdealIcon';
import BancontactIcon from '../../../assets/icons/payment-methods/BancontactIcon';
import EpsIcon from '../../../assets/icons/payment-methods/EpsIcon';
import P24Icon from '../../../assets/icons/payment-methods/P24Icon';
import BlikIcon from '../../../assets/icons/payment-methods/BlikIcon';
import TwintIcon from '../../../assets/icons/payment-methods/TwintIcon';
import VenmoIcon from '../../../assets/icons/payment-methods/VenmoIcon';
import WeroIcon from '../../../assets/icons/payment-methods/WeroIcon';
import TrustlyIcon from '../../../assets/icons/payment-methods/TrustlyIcon';
import PixIcon from '../../../assets/icons/payment-methods/PixIcon';
import NupayIcon from '../../../assets/icons/payment-methods/NupayIcon';
import LinkIcon from '../../../assets/icons/payment-methods/LinkIcon';
import classnames from 'classnames';
import { normalizeCardBrand } from '../../../services/helpers/cardBrand';
import {
  stripePaymentMethodTypeIcon,
  stripePaymentMethodTypeName,
} from '../../../services/helpers/stripePaymentMethodType';

interface Props extends Partial<PaymentOptionPropertiesInterface> {
  className?: string;
  stripe_payment_method_type?: null | string;
}

/**
 * Accepts the enum or any raw gateway spelling (`mastercard`, `amex`, `master`, `diners`, `cup`…):
 * the backend stores `payment_method_brand` as each gateway reports it, so a strict enum lookup
 * left Stripe- and Spreedly-saved Mastercards without an icon.
 */
export const getCardBrandIcon = (brand: PaymentMethodBrand | string | null | undefined): ReactElement | null => {
  const normalized = normalizeCardBrand(brand);

  if (!normalized) {
    return null;
  }

  return {
    [PaymentMethodBrand.Visa]: <VisaIcon />,
    [PaymentMethodBrand.MasterCard]: <MasterCardIcon />,
    [PaymentMethodBrand.AmericanExpress]: <AmericanExpressIcon />,
    [PaymentMethodBrand.Discover]: <DiscoverIcon />,
    [PaymentMethodBrand.DinersClub]: <DinersClubIcon />,
    [PaymentMethodBrand.Jcb]: <JcbIcon />,
    [PaymentMethodBrand.UnionPay]: <UnionPayIcon />,
    [PaymentMethodBrand.Maestro]: <MaestroIcon />,
    [PaymentMethodBrand.Dankort]: <DankortIcon />,
  }[normalized];
};

export default function PaymentMethodBrandIcons(props: Props) {
  const { payment_method_id, payment_method_brand, stripe_payment_method_type, className } = props;

  const WalletIcon = ({ className }: ClassnameAwareComponentProps) =>
    ({
      [PaymentMethod.ApplePay]: <ApplePayIcon className={className} />,
      [PaymentMethod.GooglePay]: <GooglePayIcon className={classnames(className, 'h-5')} />,
      [PaymentMethod.Link]: <LinkIcon className={className} />,
    })[payment_method_id] ?? null;

  const paymentMethodIcon =
    {
      [PaymentMethod.Cash]: <CashIcon className={className} />,
      [PaymentMethod.ApplePay]: <WalletIcon className={className} />,
      [PaymentMethod.GooglePay]: <WalletIcon className={className} />,
      [PaymentMethod.Link]: <WalletIcon className={className} />,
      [PaymentMethod.Card]: (
        <div className="flex gap-xs">
          <VisaIcon className={className} />
          <MasterCardIcon className={className} />
          <AmericanExpressIcon className={className} />
        </div>
      ),
      [PaymentMethod.Paypal]: <PayPalIcon className={className} />,
      [PaymentMethod.PaypalStandalone]: <PayPalIcon className={className} />,
      [PaymentMethod.Sofort]: <KlarnaIcon className={className} />,
      [PaymentMethod.Giropay]: <GiroPayIcon className={className} />,
      [PaymentMethod.PayByBank]: <PayByBankIcon className={className} />,
      [PaymentMethod.SepaDebit]: <SepaDebitIcon className={className} />,
      [PaymentMethod.BacsDebit]: <StripeTypeIcon type="bacs_debit" className={className} />,
      [PaymentMethod.Ideal]: <IdealIcon className={className} />,
      [PaymentMethod.Bancontact]: <BancontactIcon className={className} />,
      [PaymentMethod.Eps]: <EpsIcon className={className} />,
      [PaymentMethod.P24]: <P24Icon className={className} />,
      [PaymentMethod.Blik]: <BlikIcon className={className} />,
      [PaymentMethod.Twint]: <TwintIcon className={className} />,
      [PaymentMethod.Venmo]: <VenmoIcon className={className} />,
      [PaymentMethod.Wero]: <WeroIcon className={className} />,
      [PaymentMethod.Klarna]: <KlarnaIcon className={className} />,
      [PaymentMethod.Trustly]: <TrustlyIcon className={className} />,
      [PaymentMethod.Pix]: <PixIcon className={className} />,
      [PaymentMethod.Nupay]: <NupayIcon className={className} />,
      [PaymentMethod.StripeOther]: <StripeTypeIcon type={stripe_payment_method_type} className={className} />,
    }[payment_method_id] ?? null;

  const brandIcon = getCardBrandIcon(payment_method_brand);

  return brandIcon ? (
    <div className="flex gap-xs">
      <WalletIcon className={className} />
      {brandIcon}
    </div>
  ) : (
    paymentMethodIcon
  );
}

function StripeTypeIcon({ type, className }: { type: null | string | undefined; className?: string }) {
  return (
    <img
      src={stripePaymentMethodTypeIcon(type)}
      alt={stripePaymentMethodTypeName(type) ?? ''}
      className={classnames('h-5', className)}
    />
  );
}
